import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Brain } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { LevelCard, TraderCard, Roadmap, PausedBadge } from './LearningParts';
import { KindsGrid, SideHoursCard, BalanceCard, CoinsCard, HistoryList } from './LearningCards';
import ScalperLearningCard from './ScalperLearningCard';
import RangeLearningCard from './RangeLearningCard';
import ProfileSuggestions from './ProfileSuggestions';
import { Card } from './LearningParts';

const MODE_TR = { report: 'Sadece raporlar — ayar değiştirmez', select: 'Zararlı tür/coini geçici durdurur — boyuta dokunmaz', size: 'Boyut ve seçimi ayarlar', full: 'Boyut + stop/hedef mesafelerini ayarlar' };

/** 🧠 Öğrenme & Gelişim sayfası: seviye, trader notu, gelişim planı, karneler, geçmiş (defter seçilebilir). */
export default function LearningPage({ onSelect }) {
    const [mode, setMode] = useState('paper');
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const load = useCallback(async () => {
        try {
            const { data: d } = await apiClient.get('/autotrade/learning', { params: { mode } });
            setData(d);
            setErr(null);
        } catch (e) {
            logger.warn('learning load failed', e);
            setErr('Öğrenme raporu yüklenemedi');
        }
    }, [mode]);
    useEffect(() => {
        setData(null);
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    return (
        <div className="space-y-3" data-testid="learning-page">
            <div className="flex flex-wrap items-center gap-2 text-xs">
                <Brain className="h-4 w-4 text-violet-300" />
                <div className="flex rounded-md border border-slate-700 overflow-hidden" data-testid="learning-mode-switch">
                    {[['paper', '🧪 Sanal defter'], ['real', '🏦 Gerçek defter']].map(([m, l]) => (
                        <button key={m} type="button" onClick={() => setMode(m)} data-testid={`learning-mode-${m}`}
                                className={`px-2.5 h-7 text-[11px] font-bold transition-colors ${mode === m ? 'bg-violet-500 text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`}>{l}</button>
                    ))}
                </div>
                {data && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-100" data-testid="learning-page-mode">{MODE_TR[data.mode] || data.mode}</span>}
                {data && <span className="text-[10px] text-slate-400">son {data.days} gün · {data.sample} kapanış değerlendirildi · toplam {data.total}</span>}
                {data && <PausedBadge paused={data.paused} />}
                {!data && !err && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                {err && <span className="text-[11px] text-rose-300" data-testid="learning-error">{err}</span>}
            </div>
            {data && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <LevelCard level={data.level} />
                        <TraderCard trader={data.trader} />
                    </div>
                    <Roadmap steps={data.roadmap} />
                    <Card title="🧠 Öğrendiklerim — bunu uygula" testId="learn-profile-card" right={<span className="text-[10px] text-slate-500">normal bot · seçtiğin kural yeni girişlerde geçerli</span>}>
                        <ProfileSuggestions bot="main" mode={mode} suggestions={data.suggestions} profile={data.profile} kinds={data.kinds || []} onChanged={load} />
                    </Card>
                    <ScalperLearningCard sc={data.scalper} mode={mode} onChanged={load} />
                    <RangeLearningCard rg={data.range} />
                    <KindsGrid kinds={data.kinds} minTrades={data.min_trades} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <SideHoursCard side={data.side} hours={data.hours} reliable={data.reliable?.side_hours} />
                        <BalanceCard balance={data.balance} reliable={data.reliable?.coins_balance} />
                    </div>
                    <CoinsCard coins={data.coins} reliable={data.reliable?.coins_balance} onSelect={onSelect} />
                    <HistoryList history={data.history} skipped={data.skipped} />
                </>
            )}
        </div>
    );
}
